import React, { useEffect, useMemo, useState } from 'react';
import {
	WeatherContainer,
	WeatherSwiperSlide,
	WeatherSwiper,
} from './weatherStyles';
import { Place } from '../../../models/Place';
import { weatherCodes } from './weatherCodes';
import _ from 'lodash';
import 'swiper/css';
import 'swiper/css/free-mode';
import WeatherIcon from './WeatherIcon';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../../../state/hooks';
import { RootState } from '../../../state/store';

type WeatherProps = {
	place: Place;
};

type WeatherApiResponse = {
	daily?: {
		time: string[];
		temperature_2m_max: number[];
		temperature_2m_min: number[];
		weathercode: number[];
	};
};

const WEEKDAYS = [
	'Sunday',
	'Monday',
	'Tuesday',
	'Wednesday',
	'Thursday',
	'Friday',
	'Saturday',
] as const;

const Weather: React.FC<WeatherProps> = ({ place }) => {
	const user_prefs = useAppSelector(
		(state: RootState) => state.prefs,
		shallowEqual,
	);

	const [tempUnit, setTempUnit] = useState(user_prefs.preferred_temp_units);

	useEffect(() => {
		setTempUnit(user_prefs.preferred_temp_units);
	}, [user_prefs.preferred_temp_units]);

	const [weatherData, setWeatherData] = useState<WeatherApiResponse['daily']>();

	const convertTemperature = (temp: number, unit: string) => {
		if (unit === 'c') {
			return temp;
		} else if (unit === 'f') {
			return temp * 1.8 + 32;
		} else {
			console.warn('invalid unit');
			return temp;
		}
	};

	useEffect(() => {
		let isCancelled = false;

		const fetchWeatherData = async () => {
			try {
				const lat = place.location.lat;
				const long = place.location.lng;
				const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${long}&daily=weathercode,temperature_2m_max,temperature_2m_min&timezone=auto`;
				const response = await fetch(url);
				const json: WeatherApiResponse = await response.json();
				if (!isCancelled) {
					setWeatherData(json.daily);
				}
			} catch (error) {
				console.error('weather_fetch_failed', error);
				if (!isCancelled) {
					setWeatherData(undefined);
				}
			}
		};

		fetchWeatherData();
		return () => {
			isCancelled = true;
		};
	}, [place.location.lat, place.location.lng]);

	const weatherCards = useMemo(() => {
		const days = weatherData?.time ?? [];
		if (days.length === 0 || !weatherData) {
			return [];
		}

		return days.map((d, i) => ({
			day: WEEKDAYS[new Date(d).getDay()],
			temp_2m_max: Math.round(
				convertTemperature(weatherData.temperature_2m_max[i], tempUnit),
			),
			temp_2m_min: Math.round(
				convertTemperature(weatherData.temperature_2m_min[i], tempUnit),
			),
			weathercode: _.find(weatherCodes, [
				'code',
				weatherData.weathercode[i],
			]),
		}));
	}, [tempUnit, weatherData]);

	return (
		<WeatherContainer>
			<WeatherSwiper
				slidesPerView={'auto'}
				spaceBetween={16}
				freeMode={true}>
				{weatherCards.length > 0 ? (
					weatherCards.map((d, index) => {
						return (
							<WeatherSwiperSlide key={`${d.day}-${index}`}>
								<WeatherIcon icon={d.weathercode?.icon ?? 'clear-sky'} />
								<h3>{d.day}</h3>
								<p>{d.weathercode?.text ?? 'Weather unavailable'}</p>
								<p>{`${
									d.temp_2m_min
								}°${tempUnit.toUpperCase()}/${
									d.temp_2m_max
								}°${tempUnit.toUpperCase()}`}</p>
							</WeatherSwiperSlide>
						);
					})
				) : (
					<p>Loading weather data&hellip;</p>
				)}
			</WeatherSwiper>
		</WeatherContainer>
	);
};

export default Weather;
